/**
 * Enhanced attendance dialog component with service selection
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { trpc } from "@/lib/trpc";
import { useServiceSelection } from "../hooks/useServiceSelection";
import { PersonCountSelector } from "./services/PersonCountSelector";
import { TrainingDurationSelect } from "./services/TrainingDurationSelect";
import { ServiceCategorySection } from "./services/ServiceCategorySection";
import { CustomPriceSection } from "./services/CustomPriceSection";
import type {
  DochadzkaWithServices,
  AttendanceFormDataWithServices,
  ServiceSelectionState,
} from "../types";
import { X } from "lucide-react"; // Added import

interface Klient {
  id: number;
  alias: string;
  meno: string | null;
  priezvisko: string | null;
  odberTrening?: boolean | null;
}

interface AttendanceDialogWithServicesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingSession: DochadzkaWithServices | null;
  selectedKlientId: number | null;
  klienti?: Klient[];
  onSubmit: (data: AttendanceFormDataWithServices) => Promise<boolean>;
}

export function AttendanceDialogWithServices({
  open,
  onOpenChange,
  editingSession,
  selectedKlientId,
  klienti,
  onSubmit,
}: AttendanceDialogWithServicesProps) {
  const { t } = useTranslation();

  // Fetch training and additional services
  const { data: treningy = [] } = trpc.treningSluzby.list.useQuery();
  const { data: doplnky = [] } = trpc.doplnkoveSluzby.list.useQuery();

  // Memoize initial selection from editingSession
  const initialSelection = useMemo(() => {
    if (!editingSession) return {};

    const selection: Partial<ServiceSelectionState> = {};

    if (editingSession.treningSluzba) {
      const trening = editingSession.treningSluzba;
      selection.pocetOsob = trening.pocetOsob as "1" | "2" | "3-6";
      selection.treningId = trening.id;
    }

    if (
      editingSession.doplnky &&
      editingSession.doplnky.length > 0 &&
      doplnky.length > 0
    ) {
      const supplementy: number[] = [];
      const services: number[] = [];
      const specialne: number[] = [];
      const masaze: number[] = [];

      editingSession.doplnky.forEach(doplnok => {
        const fullDoplnok = doplnky.find(d => d.id === doplnok.id);
        if (fullDoplnok) {
          switch (fullDoplnok.kategoria) {
            case "supplement":
              supplementy.push(doplnok.id);
              break;
            case "service":
              services.push(doplnok.id);
              break;
            case "specialna":
              specialne.push(doplnok.id);
              break;
            case "masaz":
              masaze.push(doplnok.id);
              break;
          }
        }
      });

      selection.supplementy = supplementy;
      selection.services = services;
      selection.specialne = specialne;
      selection.masaze = masaze;
    }

    if (editingSession.vlastnaCena) {
      selection.vlastnaCena = Number(editingSession.vlastnaCena);
    }
    if (editingSession.vlastnyPopis) {
      selection.vlastnyPopis = editingSession.vlastnyPopis;
    }

    return selection;
  }, [editingSession, doplnky]);

  // Service selection hook with initial values
  const {
    selection,
    groupedDoplnky,
    calculateTotal,
    setPocetOsob,
    setTreningId,
    toggleDoplnok,
    setVlastnaCena,
    setVlastnyPopis,
    resetSelection,
    resetWithInitial,
  } = useServiceSelection(treningy, doplnky, initialSelection);

  // Form state
  const [klientId, setKlientId] = useState<number | null>(null);
  const [datum, setDatum] = useState<string>("");
  const [cas, setCas] = useState<string>("09:00");
  const [poznamka, setPoznamka] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when dialog opens or editing session changes
  useEffect(() => {
    if (open) {
      if (editingSession) {
        setKlientId(editingSession.klientId);
        setDatum(editingSession.datum);
        setCas(editingSession.cas || "09:00");
        setPoznamka(editingSession.poznamka || "");
        // Initial selection is already passed to the hook via initialSelection
      } else {
        setKlientId(selectedKlientId);
        setDatum(new Date().toISOString().split("T")[0]);
        setCas("09:00");
        setPoznamka("");
        resetSelection();
      }
    }
  }, [open, editingSession, selectedKlientId, resetSelection]);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false);
      // Don't reset form state here - it will be reset when dialog opens again
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    if (!klientId) {
      alert("Please select a client");
      setIsSubmitting(false);
      return;
    }

    if (!selection.treningId) {
      alert("Please select a training service");
      setIsSubmitting(false);
      return;
    }

    if (selection.vlastnaCena !== 0 && !selection.vlastnyPopis.trim()) {
      alert("Please provide a description for the custom price");
      setIsSubmitting(false);
      return;
    }

    // Collect all selected additional services
    const vybrateDoplnky = [
      ...selection.supplementy,
      ...selection.services,
      ...selection.specialne,
      ...selection.masaze,
    ];

    // Filter out any invalid doplnky IDs (ones not in the current doplnky list)
    const validDoplnkyIds = vybrateDoplnky.filter(id =>
      doplnky.some(d => d.id === id)
    );

    const data: AttendanceFormDataWithServices = {
      klientId,
      datum,
      cas,
      poznamka: poznamka || undefined,
      treningId: selection.treningId,
      vybrateDoplnky: validDoplnkyIds,
      vlastnaCena: selection.vlastnaCena,
      vlastnyPopis:
        selection.vlastnaCena !== 0 ? selection.vlastnyPopis : undefined,
    };

    console.log("Submitting attendance data:", data);

    try {
      const success = await onSubmit(data);
      if (success) {
        onOpenChange(false);
        resetSelection();
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPrice = calculateTotal();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <DialogTitle className="text-xl">
              {editingSession
                ? t("attendance:dialog.editTrainingSession")
                : t("attendance:dialog.addTrainingSession")}
            </DialogTitle>
            <DialogDescription>
              {t("attendance:dialog.selectService")}
            </DialogDescription>
          </div>
        </div>

        <form onSubmit={handleSubmit} id="attendance-form">
          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                {t("attendance:dialog.basicInfo")}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="klientId">Client *</Label>
                  <Select
                    value={klientId?.toString() || ""}
                    onValueChange={val => setKlientId(Number(val))}
                    required
                    disabled={isSubmitting || editingSession !== null}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {klienti
                        ?.filter(k => k.odberTrening)
                        .map(k => (
                          <SelectItem key={k.id} value={k.id.toString()}>
                            {k.alias} - {k.meno} {k.priezvisko}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {editingSession && (
                    <p className="text-xs text-muted-foreground">
                      Client cannot be changed when editing an existing session
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="datum">Date *</Label>
                  <Input
                    id="datum"
                    type="date"
                    value={datum}
                    onChange={e => setDatum(e.target.value)}
                    required
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cas">Time</Label>
                  <Input
                    id="cas"
                    type="time"
                    value={cas}
                    onChange={e => setCas(e.target.value)}
                    step="300"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Training Selection */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Training Service</h3>
              <PersonCountSelector
                value={selection.pocetOsob}
                onChange={setPocetOsob}
              />
              {selection.pocetOsob && (
                <TrainingDurationSelect
                  pocetOsob={selection.pocetOsob}
                  value={selection.treningId}
                  options={treningy}
                  onChange={setTreningId}
                />
              )}
            </div>

            <Separator />

            {/* Additional Services */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">
                Additional Services (Optional)
              </h3>

              {groupedDoplnky.supplement && (
                <ServiceCategorySection
                  title={t("attendance:dialog.supplements")}
                  services={groupedDoplnky.supplement}
                  selectedIds={selection.supplementy}
                  onToggle={id => toggleDoplnok("supplementy", id)}
                />
              )}

              {groupedDoplnky.service && (
                <ServiceCategorySection
                  title="Other Services"
                  services={groupedDoplnky.service}
                  selectedIds={selection.services}
                  onToggle={id => toggleDoplnok("services", id)}
                />
              )}

              {groupedDoplnky.specialna && (
                <ServiceCategorySection
                  title="Special Rates"
                  services={groupedDoplnky.specialna}
                  selectedIds={selection.specialne}
                  onToggle={id => toggleDoplnok("specialne", id)}
                />
              )}

              {groupedDoplnky.masaz && (
                <ServiceCategorySection
                  title="Massages"
                  services={groupedDoplnky.masaz}
                  selectedIds={selection.masaze}
                  onToggle={id => toggleDoplnok("masaze", id)}
                />
              )}
            </div>

            <Separator />

            {/* Custom Price */}
            <CustomPriceSection
              vlastnaCena={selection.vlastnaCena}
              vlastnyPopis={selection.vlastnyPopis}
              onPriceChange={setVlastnaCena}
              onDescriptionChange={setVlastnyPopis}
            />

            <Separator />

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="poznamka">Notes (Optional)</Label>
              <Textarea
                id="poznamka"
                value={poznamka}
                onChange={e => setPoznamka(e.target.value)}
                placeholder="Additional notes..."
                rows={3}
                disabled={isSubmitting}
              />
            </div>

            {/* Price Breakdown */}
            <div className="space-y-3 p-4 bg-muted rounded-lg">
              <h3 className="text-lg font-semibold">Price Breakdown</h3>
              <div className="space-y-2 text-sm">
                {selection.treningId &&
                  (() => {
                    const trening = treningy.find(
                      t => t.id === selection.treningId
                    );
                    return trening ? (
                      <div className="flex justify-between">
                        <span>{trening.nazov}</span>
                        <span className="font-medium">
                          {Number(trening.cena).toFixed(2)} €
                        </span>
                      </div>
                    ) : null;
                  })()}

                {[
                  ...selection.supplementy,
                  ...selection.services,
                  ...selection.specialne,
                  ...selection.masaze,
                ].map(id => {
                  const doplnok = doplnky.find(d => d.id === id);
                  return doplnok ? (
                    <div key={id} className="flex justify-between">
                      <span>{doplnok.nazov}</span>
                      <span className="font-medium">
                        {Number(doplnok.cena).toFixed(2)} €
                      </span>
                    </div>
                  ) : null;
                })}

                {selection.vlastnaCena !== 0 && (
                  <div className="flex justify-between">
                    <span>{selection.vlastnyPopis || "Custom Price"}</span>
                    <span className="font-medium">
                      {selection.vlastnaCena > 0 ? "+" : ""}
                      {selection.vlastnaCena.toFixed(2)} €
                    </span>
                  </div>
                )}

                <Separator className="my-2" />

                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Price:</span>
                  <Badge variant="default" className="text-lg px-4 py-2">
                    {totalPrice.toFixed(2)} €
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Sticky footer with buttons */}
          <div className="sticky bottom-0 bg-background border-t pt-4 mt-4 -mx-6 px-6">
            <div className="flex justify-between">
              <div className="text-sm text-muted-foreground">
                {editingSession
                  ? "Update existing training session"
                  : "Create new training session"}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                    resetSelection();
                  }}
                  disabled={isSubmitting}
                >
                  {t("common:cancel")}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? t("common:loading")
                    : editingSession
                      ? t("common:update")
                      : t("common:create")}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
